import { lazy, Suspense, useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import {
  Anchor,
  ArrowRight,
  Award,
  Fish,
  Handshake,
  Leaf,
  PackageCheck,
  Quote,
  Recycle,
  Sailboat,
  ShieldCheck,
  Snowflake,
  Sunrise,
  TrendingUp,
  Truck,
} from "lucide-react";
import PageTransition from "../components/PageTransition";
import ScrollReveal from "../components/ScrollReveal";
import Wave from "../components/Wave";
import JumpingFish from "../components/JumpingFish";
import Bubbles from "../components/Bubbles";
import HeroSkeleton from "../components/HeroSkeleton";
import ParallaxLayer from "../components/ParallaxLayer";
import AppDownloadSection from "../components/AppDownloadSection";
import PressLogoStrip from "../components/PressLogoStrip";
import PromoBanners from "../components/PromoBanners";
import usePrefersReducedMotion from "../hooks/usePrefersReducedMotion";
import useAssetReady from "../hooks/useAssetReady";
import useWebGLSupport from "../hooks/useWebGLSupport";
import useStructuredData from "../hooks/useStructuredData";
import { trackEvent } from "../utils/analytics";
import { buildOrganizationSchema } from "../utils/structuredData";
import { getWikimediaSrcSet } from "../utils/wikimediaImage";
import { seafoodItems } from "../data/seafood";
import { STATS } from "../data/stats";

const HERO_BG = "/hero-bg.webp";

// ZP-91: three.js/@react-three/fiber are large and only this hero needs
// them — lazy-loaded exactly like /locations' Leaflet split in App.tsx, so
// every other route (and every visitor who never qualifies for the 3D
// scene at all — no WebGL, low-power, reduced-motion) never pays for this
// chunk. The Suspense boundary for it lives here, scoped to this section,
// never near App.tsx's routing — a Suspense between AnimatePresence and
// the keyed <Routes> there would silently kill every page's exit
// transition (see jira/README.md's ZP-22 note).
const HeroScene3D = lazy(() => import("../components/HeroScene3D"));

const STEPS = [
  { icon: Sunrise, key: "caughtAtDawn" },
  { icon: Snowflake, key: "icedAndPacked" },
  { icon: Truck, key: "deliveredFresh" },
  { icon: PackageCheck, key: "youEnjoy" },
] as const;

const VALUES = [
  { icon: Leaf, key: "noOverfishing" },
  { icon: ShieldCheck, key: "fullyTraceable" },
  { icon: Recycle, key: "lowWastePackaging" },
  { icon: Award, key: "fairTradePricing" },
] as const;

const TESTIMONIALS = [
  {
    quote: "The vanjaram arrived still cold, and the flavor was on another level. I can taste the difference from market fish.",
    name: "Priya R.",
    role: "Home Cook, Mumbai",
  },
  {
    quote: "As a chef, traceability matters to me. Knowing exactly which boat caught my karimeen is a game changer.",
    name: "Arjun Nair",
    role: "Executive Chef, Kochi",
  },
  {
    quote: "Ordered spiny lobster for a dinner party and it showed up fresh and full of flavor. Delivery was faster than I expected.",
    name: "Meera S.",
    role: "Customer, Chennai",
  },
];

export default function Home() {
  const { t } = useTranslation("home");
  const reducedMotion = usePrefersReducedMotion();
  const heroReady = useAssetReady(HERO_BG);
  const webglSupported = useWebGLSupport();
  const organizationSchema = useMemo(() => buildOrganizationSchema(window.location.origin), []);
  useStructuredData(organizationSchema);
  const heroTransition = (delay: number) =>
    reducedMotion ? { duration: 0 } : { duration: 0.7, delay };

  // ZP-91 state machine. The poster (HERO_BG, above) already shows first
  // unconditionally and IS the polished fallback the ticket's AC asks for
  // — nothing here ever blocks on or races the poster. `deferMount` adds
  // one more beat past "poster has painted": an idle-callback tick, so the
  // 3D scene's own setup work never competes with the poster's LCP/TBT
  // window even by a few milliseconds. Only once WebGL is confirmed
  // available, the device isn't flagged low-power, motion is allowed, AND
  // that idle beat has passed do we even lazy-mount the component — and
  // even then it stays invisible (opacity 0) until its own onReady fires
  // after a real frame has painted, so the crossfade never shows a blank
  // or half-initialized canvas.
  const [deferMount, setDeferMount] = useState(false);
  const [scene3DReady, setScene3DReady] = useState(false);
  const handleScene3DReady = useCallback(() => setScene3DReady(true), []);

  useEffect(() => {
    if (!heroReady) return;
    const win = window as Window & {
      requestIdleCallback?: (cb: () => void) => number;
      cancelIdleCallback?: (handle: number) => void;
    };
    if (win.requestIdleCallback) {
      const handle = win.requestIdleCallback(() => setDeferMount(true));
      return () => win.cancelIdleCallback?.(handle);
    }
    const timer = window.setTimeout(() => setDeferMount(true), 200);
    return () => window.clearTimeout(timer);
  }, [heroReady]);

  const shouldMount3D = webglSupported && !reducedMotion && deferMount;

  return (
    <PageTransition>
      {/* Hero */}
      <section className="relative min-h-[720px] overflow-hidden pt-32 pb-16 text-foam-50 sm:min-h-[840px] sm:pt-40 sm:pb-20">
        {/* Parallax depth (ZP-92): the background image drifts slightly
            slower than the page scrolls past this section, while the
            gradient overlay and foreground content stay static — the
            classic depth cue. -inset-y-16 overhangs the image 64px past the
            section's own edges (well past ParallaxLayer's default ±60px
            drift range) so the shift never exposes a gap at the top/bottom
            edge; the section's own `overflow-hidden` clips the overhang. */}
        <ParallaxLayer className="absolute -inset-y-16 inset-x-0">
          <img
            src={HERO_BG}
            alt=""
            role="presentation"
            loading="eager"
            fetchPriority="high"
            decoding="async"
            width={1600}
            height={900}
            className="h-full w-full object-cover"
          />
        </ParallaxLayer>

        {/* ZP-91: 3D scene crossfades in over the poster (never before its
            own onReady fires), sitting below the gradient scrim so text
            legibility is identical whether or not the scene ends up
            mounted. No-WebGL / low-power / reduced-motion visitors simply
            never render this block — the poster above stays as-is and IS
            the fallback. */}
        {shouldMount3D && (
          <Suspense fallback={null}>
            <motion.div
              className="absolute inset-0"
              aria-hidden="true"
              initial={{ opacity: 0 }}
              animate={{ opacity: scene3DReady ? 1 : 0 }}
              transition={{ duration: 0.8, ease: "easeOut" }}
            >
              <HeroScene3D onReady={handleScene3DReady} />
            </motion.div>
          </Suspense>
        )}

        {/* ZP-118: diagonal deep-navy-to-sunlit-cyan scrim (was a flat top-to-bottom
            950→800 fade) — still opaque enough over the top-left/centre where the title,
            subtitle and CTAs sit (from-950/95 via-900/85) for foam-50 text contrast, but
            lets a hint of bright ocean-500 show through the bottom-right corner, which only
            ever holds the low-opacity decorative fish/bubble icons, never real text. */}
        <div className="absolute inset-0 bg-gradient-to-br from-ocean-950/95 via-ocean-900/85 to-ocean-500/30" />

        <AnimatePresence>{!heroReady && <HeroSkeleton />}</AnimatePresence>

        {!reducedMotion && (
          <div className="pointer-events-none absolute inset-0">
            <Bubbles count={16} />
            {/* The 3D scene's own swimming fish + drifting camera cover this
                same "life in the water" motif once it's on screen — keeping
                these 2D icons layered on top too would just be visual
                clutter, so they only render until the 3D scene is
                confirmed showing (or forever, on the fallback path where
                shouldMount3D never becomes true). */}
            {!scene3DReady && (
              <>
                <motion.div
                  animate={{ y: [0, -14, 0], rotate: [0, 3, 0] }}
                  transition={{ duration: 7, repeat: Infinity, ease: "easeInOut" }}
                  className="absolute right-[8%] top-28 hidden sm:block"
                >
                  <Fish className="h-16 w-16 text-teal-400/30" strokeWidth={1} />
                </motion.div>
                <motion.div
                  animate={{ y: [0, 12, 0] }}
                  transition={{ duration: 6, repeat: Infinity, ease: "easeInOut", delay: 0.5 }}
                  className="absolute left-[6%] top-1/2 hidden sm:block"
                >
                  <Sailboat className="h-20 w-20 text-teal-300/20" strokeWidth={1} />
                </motion.div>
                <motion.div
                  animate={{ y: [0, -10, 0] }}
                  transition={{ duration: 5, repeat: Infinity, ease: "easeInOut", delay: 1 }}
                  className="absolute right-[20%] bottom-16 hidden sm:block"
                >
                  <Anchor className="h-12 w-12 text-coral-400/20" strokeWidth={1} />
                </motion.div>
                <JumpingFish className="left-[12%] bottom-20 hidden sm:block" size={44} delay={0.5} />
                <JumpingFish
                  className="right-[28%] bottom-10 hidden sm:block"
                  size={32}
                  delay={2.4}
                  duration={2.2}
                  color="var(--color-coral-400)"
                  flip
                />
              </>
            )}
          </div>
        )}

        {heroReady && (
          <div className="relative mx-auto max-w-6xl px-6 text-center">
            <motion.img
              src="/logo.png"
              alt="ZenFresh"
              width={64}
              height={64}
              initial={reducedMotion ? false : { opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={heroTransition(0)}
              className="mx-auto h-16 w-16 object-contain"
            />

            <motion.span
              initial={reducedMotion ? false : { opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={heroTransition(0.05)}
              className="mt-4 inline-flex items-center gap-2 rounded-full bg-white/10 px-4 py-1.5 text-xs font-medium tracking-wide text-teal-200 ring-1 ring-white/10"
            >
              <Anchor className="h-3.5 w-3.5" />
              {t("hero.badge")}
            </motion.span>

            <motion.h1
              initial={reducedMotion ? false : { opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={heroTransition(0.1)}
              className="mt-6 font-display text-4xl font-bold leading-[1.1] sm:text-6xl"
            >
              {t("hero.titleLine1")}
              <br />
              <span className="text-shimmer">{t("hero.titleLine2")}</span>
            </motion.h1>

            <motion.p
              initial={reducedMotion ? false : { opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={heroTransition(0.2)}
              className="mx-auto mt-6 max-w-xl text-base text-foam-100/70 sm:text-lg"
            >
              {t("hero.subtitle")}
            </motion.p>

            <motion.div
              initial={reducedMotion ? false : { opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={heroTransition(0.3)}
              className="mt-10 flex flex-col items-center justify-center gap-4 sm:flex-row"
            >
              <Link
                to="/menu"
                className="group inline-flex items-center gap-2 rounded-full bg-teal-400 px-7 py-3.5 text-sm font-semibold text-ocean-950 transition-transform hover:scale-105"
              >
                {t("hero.orderNow")}
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </Link>
              <a
                href="#how-it-works"
                className="inline-flex items-center gap-2 rounded-full px-7 py-3.5 text-sm font-semibold text-foam-50 ring-1 ring-white/20 transition-colors hover:bg-white/10"
              >
                {t("hero.howItWorks")}
              </a>
            </motion.div>

            <motion.div
              initial={reducedMotion ? false : { opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={heroTransition(0.35)}
              className="mt-6 inline-flex flex-wrap items-center justify-center gap-2 rounded-2xl bg-white/5 p-1.5 ring-1 ring-white/10"
            >
              <Link
                to="/partnerships"
                onClick={() => trackEvent("partner_cta_click", { location: "hero" })}
                className="inline-flex items-center gap-2 rounded-xl bg-white/10 px-4 py-2 text-sm font-semibold text-foam-50 transition-colors hover:bg-white/20"
              >
                <Handshake className="h-4 w-4 text-teal-300" />
                {t("hero.partnerWithUs")}
              </Link>
              <Link
                to="/press-investors"
                onClick={() => trackEvent("investor_cta_click", { location: "hero" })}
                className="inline-flex items-center gap-2 rounded-xl bg-white/10 px-4 py-2 text-sm font-semibold text-foam-50 transition-colors hover:bg-white/20"
              >
                <TrendingUp className="h-4 w-4 text-teal-300" />
                {t("hero.investorRelations")}
              </Link>
            </motion.div>
          </div>
        )}
      </section>

      <Wave fill="var(--color-foam-50)" />

      <div className="bg-foam-50 dark:bg-ocean-950"><PromoBanners placement="home" className="mx-auto max-w-6xl px-6 pt-2 pb-6" /></div>

      <PressLogoStrip />

      {/* Stats */}
      <section className="bg-foam-50 pb-4 dark:bg-ocean-950">
        <div className="mx-auto grid max-w-5xl grid-cols-2 gap-6 px-6 sm:grid-cols-4">
          {heroReady
            ? STATS.map((stat, i) => (
                <ScrollReveal key={stat.label} delay={i * 0.08} className="text-center">
                  <p className="font-display text-3xl font-bold text-ocean-900 sm:text-4xl dark:text-foam-50">{stat.value}</p>
                  <p className="mt-1 text-xs font-medium uppercase tracking-wide text-ocean-700/70 sm:text-sm dark:text-foam-100/70">
                    {stat.label}
                  </p>
                </ScrollReveal>
              ))
            : Array.from({ length: 4 }).map((_, i) => (
                <div key={i} aria-hidden="true" className="flex flex-col items-center gap-2 py-1">
                  <div className="h-8 w-16 animate-pulse rounded-lg bg-ocean-900/10 sm:h-10 sm:w-20 dark:bg-white/10" />
                  <div className="h-3 w-20 animate-pulse rounded bg-ocean-900/10 sm:h-4 dark:bg-white/10" />
                </div>
              ))}
        </div>
      </section>

      {/* How it works */}
      <section id="how-it-works" className="mx-auto max-w-6xl px-6 py-24 dark:bg-ocean-950">
        <ScrollReveal className="mx-auto max-w-xl text-center">
          <h2 className="font-display text-3xl font-bold text-ocean-950 sm:text-4xl dark:text-foam-50">{t("howItWorks.title")}</h2>
          <p className="mt-4 text-ocean-800/70 dark:text-foam-100/70">{t("howItWorks.subtitle")}</p>
        </ScrollReveal>

        <div className="mt-16 grid gap-8 sm:grid-cols-2 lg:grid-cols-4">
          {STEPS.map((step, i) => (
            <ScrollReveal key={step.key} delay={i * 0.1}>
              <div className="relative rounded-2xl bg-white p-6 shadow-sm ring-1 ring-ocean-900/5 dark:bg-ocean-900 dark:ring-white/10">
                <span className="absolute -top-3 -left-3 flex h-8 w-8 items-center justify-center rounded-full bg-ocean-900 font-display text-xs font-bold text-foam-50">
                  {i + 1}
                </span>
                <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-teal-400/15 text-teal-500 dark:bg-teal-400/20 dark:text-teal-300">
                  <step.icon className="h-6 w-6" strokeWidth={1.5} />
                </div>
                <h3 className="mt-4 font-display text-lg font-semibold text-ocean-950 dark:text-foam-50">
                  {t(`howItWorks.steps.${step.key}.title`)}
                </h3>
                <p className="mt-2 text-sm leading-relaxed text-ocean-800/70 dark:text-foam-100/70">
                  {t(`howItWorks.steps.${step.key}.body`)}
                </p>
              </div>
            </ScrollReveal>
          ))}
        </div>
      </section>

      {/* Featured catch */}
      <section className="relative overflow-hidden bg-ocean-900 py-24 text-foam-50">
        <Bubbles count={10} className="opacity-60" />
        <JumpingFish className="right-[6%] top-10 hidden lg:block" size={36} delay={1.2} duration={2.4} />
        <div className="relative mx-auto max-w-6xl px-6">
          <ScrollReveal className="flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-end">
            <div>
              <h2 className="font-display text-3xl font-bold sm:text-4xl">{t("featuredCatch.title")}</h2>
              <p className="mt-3 max-w-md text-foam-100/70">{t("featuredCatch.subtitle")}</p>
            </div>
            <Link
              to="/menu"
              className="inline-flex items-center gap-2 whitespace-nowrap text-sm font-semibold text-teal-300 hover:text-teal-200"
            >
              {t("featuredCatch.viewFullMenu")}
              <ArrowRight className="h-4 w-4" />
            </Link>
          </ScrollReveal>

          <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {seafoodItems.slice(0, 4).map((item, i) => (
              <ScrollReveal key={item.id} delay={i * 0.08}>
                <div className="group overflow-hidden rounded-2xl bg-white/5 ring-1 ring-white/10 transition-colors hover:bg-white/10">
                  <div className="h-32 w-full overflow-hidden">
                    <img
                      src={item.image}
                      srcSet={getWikimediaSrcSet(item.image)}
                      alt={item.name}
                      title={item.credit}
                      loading="lazy"
                      className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
                    />
                  </div>
                  <div className="p-5">
                    <h3 className="font-display font-semibold">{item.name}</h3>
                    <p className="mt-1 text-sm text-foam-100/60">{item.description}</p>
                    <div className="mt-4 flex items-center justify-between">
                      <span className="font-display text-lg font-bold text-teal-300">{item.price}</span>
                      <span className="text-xs text-foam-100/50">{item.unit}</span>
                    </div>
                  </div>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* Sustainability / values */}
      <section className="mx-auto max-w-6xl px-6 py-24 dark:bg-ocean-950">
        <div className="grid gap-12 lg:grid-cols-2 lg:items-center">
          <ScrollReveal>
            <span className="text-xs font-semibold uppercase tracking-widest text-teal-500">{t("whyZenFresh.eyebrow")}</span>
            <h2 className="mt-3 font-display text-3xl font-bold text-ocean-950 sm:text-4xl dark:text-foam-50">
              {t("whyZenFresh.title")}
            </h2>
            <p className="mt-4 text-ocean-800/70 dark:text-foam-100/70">{t("whyZenFresh.body")}</p>
            <Link
              to="/about"
              className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-ocean-900 hover:text-teal-600 dark:text-foam-50"
            >
              {t("whyZenFresh.readOurStory")}
              <ArrowRight className="h-4 w-4" />
            </Link>
          </ScrollReveal>

          <div className="grid gap-5 sm:grid-cols-2">
            {VALUES.map((value, i) => (
              <ScrollReveal key={value.key} delay={i * 0.08}>
                <div className="rounded-2xl bg-foam-100 p-5 ring-1 ring-ocean-900/5 dark:bg-ocean-900 dark:ring-white/10">
                  <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-ocean-900/5 text-ocean-800 dark:bg-white/10 dark:text-foam-100">
                    <value.icon className="h-5 w-5" strokeWidth={1.5} />
                  </div>
                  <h3 className="mt-4 font-display font-semibold text-ocean-950 dark:text-foam-50">
                    {t(`whyZenFresh.values.${value.key}.title`)}
                  </h3>
                  <p className="mt-1.5 text-sm leading-relaxed text-ocean-800/70 dark:text-foam-100/70">
                    {t(`whyZenFresh.values.${value.key}.body`)}
                  </p>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      <AppDownloadSection />

      {/* Testimonials */}
      <section className="bg-foam-100 py-24 dark:bg-ocean-900">
        <div className="mx-auto max-w-6xl px-6">
          <ScrollReveal className="mx-auto max-w-xl text-center">
            <h2 className="font-display text-3xl font-bold text-ocean-950 sm:text-4xl dark:text-foam-50">
              {t("testimonials.title")}
            </h2>
          </ScrollReveal>

          <div className="mt-12 grid gap-6 md:grid-cols-3">
            {TESTIMONIALS.map((testimonial, i) => (
              <ScrollReveal key={testimonial.name} delay={i * 0.1}>
                <div className="flex h-full flex-col rounded-2xl bg-white p-6 shadow-sm ring-1 ring-ocean-900/5 dark:bg-ocean-950 dark:ring-white/10">
                  <Quote className="h-6 w-6 text-teal-400" />
                  <p className="mt-4 flex-1 text-sm leading-relaxed text-ocean-800/80 dark:text-foam-100/80">{testimonial.quote}</p>
                  <div className="mt-5">
                    <p className="font-display text-sm font-semibold text-ocean-950 dark:text-foam-50">{testimonial.name}</p>
                    <p className="text-xs text-ocean-800/50 dark:text-foam-100/50">{testimonial.role}</p>
                  </div>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA banner — ZP-118: same deep-navy-to-bright-cyan mood as the hero scrim above
          (was a near-flat 900→950 fade); the centred text block sits at ~the via-ocean-800
          midpoint of the diagonal (foam-50 there is ~11.6:1, comfortably AA), only the
          empty corners reach the brighter ocean-600 end. */}
      <section className="relative overflow-hidden bg-gradient-to-br from-ocean-950 via-ocean-800 to-ocean-600 py-24 text-center text-foam-50">
        <Bubbles count={12} />
        <JumpingFish className="left-[10%] bottom-6 hidden sm:block" size={40} delay={0.8} />
        <JumpingFish
          className="right-[14%] bottom-12 hidden sm:block"
          size={28}
          delay={2.8}
          duration={2}
          color="var(--color-coral-400)"
          flip
        />
        <div className="relative mx-auto max-w-2xl px-6">
          <ScrollReveal>
            <h2 className="font-display text-3xl font-bold sm:text-4xl">{t("finalCta.title")}</h2>
            <p className="mt-4 text-foam-100/70">{t("finalCta.body")}</p>
            <Link
              to="/menu"
              className="mt-8 inline-flex items-center gap-2 rounded-full bg-teal-400 px-8 py-3.5 text-sm font-semibold text-ocean-950 transition-transform hover:scale-105"
            >
              {t("finalCta.exploreMenu")}
              <ArrowRight className="h-4 w-4" />
            </Link>
          </ScrollReveal>
        </div>
      </section>
    </PageTransition>
  );
}
